import {
  CaretDownOutlined,
  CloseCircleFilled,
  ExclamationCircleFilled,
  PlusCircleFilled,
  SearchOutlined,
} from '@ant-design/icons';
import {
  Button,
  Col,
  Dropdown,
  Input,
  List,
  Menu,
  Row,
  Table,
  Tooltip,
  message,
} from 'antd';
import moment from 'moment';
import {
  useState,
  useEffect,
  useRef,
  useCallback,
  FC,
  MutableRefObject,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { fetchAllAPIKeys } from '../../api/apiKey';
import { fetchGroupsWithDescription } from '../../api/groups';
import { GeneratedAPIKeyInstance } from '../../components';
import { SearchInput } from '../../components/SearchInput';
import { APIKeyDetails } from '../../constants/types';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import {
  diffToLocalTimeZone,
  formatDateToUTC,
} from '../../utils/dateFormatter';
import {
  formatErrorMessage,
  getExpiringKeyMessage,
} from '../../utils/errorMessage';
import { getParentOrigin } from '../../utils/getKeycloakConfig';
import { dateSorter } from '../../utils/sorter';
import {
  APIKeyWarning,
  DropdownButton,
  DropdownContainer,
  EmptySearchResults,
  FiltersContainer,
  GroupListModal,
  ListContainer,
  SelectionButton,
  SelectionContainer,
  SelectionItem,
  SelectionItems,
  SelectionTitle,
  StyledCheckbox,
  StyledCheckboxGroup,
  StyledModal,
  Text,
  Wrapper,
} from '../styles';

const StyledSearchInput = styled(props => <SearchInput {...props} />)``;

interface GroupedApiKeys {
  name: string;
  description: string;
  apiKeyCount: number;
  lastUsedAt: string;
  hasValidKeys: boolean;
  expiringKeyMessage: string;
  createdAt: string;
  updatedAt: string;
  keyList: APIKeyDetails[];
}

interface VisibleAPIKeyGroups {
  name: string;
  groups?: string[];
}

interface GroupsOptions {
  value: string;
  label: string;
}

interface FilterGroupsOptions {
  key: string;
  items: GroupsOptions[];
}

const APIKey: FC = () => {
  const groupFilterContainer = useRef() as MutableRefObject<HTMLDivElement>;

  const history = useHistory();
  const intl = useIntl();
  const parentOrigin = getParentOrigin();
  const [loading, setLoading] = useState(false);
  const [newApiKey, setNewApiKey] = useState<string>('');
  const [apiKeysData, setAPIKeysData] = useState<APIKeyDetails[]>([]);
  const [groupedAPIKeys, setGroupedAPIKeys] = useState<GroupedApiKeys[]>([]);
  const [filters, setFilters] = useState<any>({
    group: [],
    masked: '',
    name: '',
  });
  const [groupOptions, setGroupOptions] = useState<GroupsOptions[]>([]);
  const [filteredGroupOptions, setFilteredGroupOptions] =
    useState<FilterGroupsOptions>({
      key: '',
      items: [],
    });
  const [isFilteringGroups, setIsFilteringGroups] = useState(false);
  const [visibleUserGroups, setVisibleUserGroups] =
    useState<VisibleAPIKeyGroups | null>(null);

  useOnClickOutside(groupFilterContainer, () => {
    setIsFilteringGroups(false);
    setFilteredGroupOptions({
      key: '',
      items: [],
    });
  });

  const columns = [
    {
      title: intl.formatMessage({
        id: 'ApiKeys.column.NewApiKeyGroup',
      }),
      dataIndex: 'name',
      key: 'name',
      sorter: (a: APIKeyDetails, b: APIKeyDetails) => {
        if (a.name.toLowerCase() > b.name.toLowerCase()) return 1;
        else if (a.name.toLowerCase() < b.name.toLowerCase()) return -1;
        else return 0;
      },
      showSorterTooltip: false,
      render: (name: string, record: any) => (
        <Row wrap={false}>
          <APIKeyWarning>
            {(!record.hasValidKeys ||
              (record.hasValidKeys && record.expiringKeyMessage)) && (
              <Tooltip
                placement="topLeft"
                data-testid="api-key-group_warning"
                title={
                  !record.hasValidKeys
                    ? intl.formatMessage({
                        id: 'ApiKeys.tooltip.hasValidKeys',
                      })
                    : record.expiringKeyMessage
                }
              >
                <ExclamationCircleFilled data-testid="api-key-group_warning" />
              </Tooltip>
            )}
          </APIKeyWarning>
          <Col>{name}</Col>
        </Row>
      ),
      width: '20%',
    },
    {
      title: intl.formatMessage({
        id: 'ApiKeys.form.label.description',
      }),
      dataIndex: 'description',
      key: 'description',
      render: (description: string) => <Text>{description}</Text>,
    },
    {
      title: intl.formatMessage({
        id: 'ApiKeys.column.ApiKeyCount',
      }),
      dataIndex: 'apiKeyCount',
      key: 'apiKeyCount',
      showSorterTooltip: false,
      sorter: (a: any, b: any) => a.apiKeyCount - b.apiKeyCount,
      render: (apiKeyCount: string) => (
        <div style={{ textAlign: 'center' }}>{apiKeyCount}</div>
      ),
      width: '160px',
    },
    {
      title: intl.formatMessage({
        id: 'ApiKeys.column.lastUsed',
      }),
      dataIndex: 'lastUsedAt',
      key: 'lastUsedAt',
      sorter: dateSorter('lastUsedAt'),
      showSorterTooltip: false,
      render: (lastUsedAt: string) => (
        <div style={{ textAlign: 'center' }}>
          {lastUsedAt ? (
            <Tooltip
              title={intl.formatMessage(
                {
                  id: 'ApiKeys.tooltip.lastUsedGroup',
                },
                { date: formatDateToUTC(lastUsedAt) },
              )}
            >
              {diffToLocalTimeZone(lastUsedAt).text}
            </Tooltip>
          ) : (
            'N/A'
          )}
        </div>
      ),
      width: '150px',
    },
    {
      title: intl.formatMessage({
        id: 'ApiKeys.column.userGroups',
      }),
      key: 'actions',
      render: (_: any, record: APIKeyDetails) => {
        if (record?.groups && record?.groups.length > 0) {
          return (
            <Link
              to="/"
              data-testid={`api-key-group_viewGroups-${record.name}`}
              className="link"
              onClick={e => {
                e.preventDefault();
                setVisibleUserGroups({
                  name: record.name,
                  groups: record.groups,
                });
                e.stopPropagation();
              }}
            >
              <FormattedMessage id="ApiKeys.column.userGroups.view" />
            </Link>
          );
        } else {
          return null;
        }
      },
      width: '150px',
    },
  ];

  const searchParams = useLocation().search;
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    const apikey = params.get('apikey');

    if (apikey) {
      setNewApiKey(apikey);
    }
  }, [searchParams]);

  /**
   * Formats the API keys response object into a grouped object for
   * table rendering.
   *
   * @param {APIKeyDetails[]} items
   * List of API keys from backend
   *
   * @returns
   */
  const extractGroups = useCallback(
    (items: APIKeyDetails[]): GroupedApiKeys[] => {
      const groupedObject = items.reduce((acc: any, curr) => {
        (acc[curr.name] = acc[curr.name] || []).push(curr);
        return acc;
      }, {});

      return Object.keys(groupedObject).map((groupName, index) => ({
        key: index,
        name: groupName,
        description: groupedObject[groupName][0]?.description || '',
        apiKeyCount: groupedObject[groupName].length,
        lastUsedAt: groupedObject[groupName].reduce(
          (latest: APIKeyDetails, current: APIKeyDetails) => {
            if (!current.lastUsedAt) {
              return latest;
            }
            if (
              !latest ||
              moment(current.lastUsedAt).isAfter(moment(latest.lastUsedAt))
            ) {
              return current;
            }
            return latest;
          },
          undefined,
        )?.lastUsedAt,
        hasValidKeys: !!groupedObject[groupName].find(
          (keyDetails: APIKeyDetails) =>
            keyDetails.status === 'Active' ||
            keyDetails.status === 'Active in future',
        ),
        expiringKeyMessage: groupedObject[groupName].reduce(
          (acc: any, curr: APIKeyDetails) => {
            const messageDetails = getExpiringKeyMessage(
              curr,
              intl.formatMessage({
                id: 'ApiKeys.KeyGroup.expire',
              }),
            );
            if (!acc.message) {
              return messageDetails;
            } else if (
              acc.message &&
              acc.days > messageDetails.days &&
              !!messageDetails.message
            ) {
              return messageDetails;
            }
            return acc;
          },
          { message: '', days: 0 },
        ).message,
        createdAt: groupedObject[groupName][0]?.createdAt,
        updatedAt: groupedObject[groupName][0]?.updatedAt,
        keyList: groupedObject[groupName],
        groups: groupedObject[groupName][0]?.groups,
      }));
    },
    [intl],
  );

  useEffect(() => {
    setLoading(true);

    fetchAllAPIKeys()
      .then(apiKeys => {
        apiKeys.sort((a, b) => +new Date(b.updatedAt) - +new Date(a.updatedAt));

        setAPIKeysData(apiKeys);
        setGroupedAPIKeys(extractGroups(apiKeys));
      })
      .catch((error: any) => {
        message.error(formatErrorMessage(error?.data?.errorCode), 2);
      })
      .finally(() => setLoading(false));

    fetchGroupsWithDescription().then(groups => {
      const keycloakGroups = groups.map(group => ({
        label: group.name,
        value: group.name,
      }));
      setGroupOptions(keycloakGroups);
    });

    return () => {
      setGroupOptions([]);
    };
  }, [extractGroups]);

  useEffect(() => {
    let matchedItems = apiKeysData;
    if (filters.group.length > 0) {
      matchedItems = matchedItems.filter(apikey =>
        apikey.groups?.some(apikeygroup => filters.group.includes(apikeygroup)),
      );
    }

    if (filters.name) {
      matchedItems = matchedItems.filter(
        item =>
          item.name?.toLowerCase().includes(filters.name) ||
          item.masked.includes(filters.masked),
      );
    }
    setGroupedAPIKeys(extractGroups(matchedItems));
  }, [filters, apiKeysData, extractGroups]);

  const handleSearchApiKey = (keyword: string) => {
    const regex = /[0-9a-zA-Z-]/g;
    let newKeyword = keyword;
    let toAsterisk = '';

    if (keyword.length === 36) {
      toAsterisk = keyword.slice(3, -3);
      newKeyword =
        keyword.slice(0, 3) +
        toAsterisk.replace(regex, '*') +
        keyword.slice(keyword.length - 3);
    } else {
      toAsterisk = keyword.slice(3);
      newKeyword =
        keyword.slice(0, 3) + toAsterisk.replace(regex, '*').slice(0, 30);
    }

    setFilters((prevState: any) => ({
      ...prevState,
      masked: newKeyword,
      name: keyword.toLowerCase(),
    }));
  };

  const handleSearchGroupFilters = (keyword: string) => {
    const matchedItems = groupOptions.filter(item =>
      item.value?.toLowerCase().includes(keyword.toLowerCase()),
    );

    setFilteredGroupOptions({
      key: keyword,
      items: matchedItems,
    });
  };

  const deleteItemFromFilter = (index: number) => {
    const list = [...filters.group];
    list.splice(index, 1);
    setFilters((prevState: any) => ({
      ...prevState,
      group: list,
    }));
  };

  /**
   * Renders the options for user group filters.
   */
  const userGroupsOptions = () => {
    const options = filteredGroupOptions.key
      ? filteredGroupOptions.items
      : groupOptions;

    const setSelectedFilter = (e: any) => {
      const { checked, value } = e.target;
      let allFilters = [...filters.group];

      if (checked) {
        allFilters = [...allFilters, value];
      } else {
        allFilters = allFilters.filter(filter => filter !== value);
      }
      setFilters((prevState: any) => ({
        ...prevState,
        group: allFilters,
      }));
      setIsFilteringGroups(false);
      setFilteredGroupOptions({
        key: '',
        items: [],
      });
    };

    return (
      <DropdownContainer ref={groupFilterContainer}>
        <Menu>
          <Menu.Item key="groups-options-search">
            <Input
              placeholder="Search group"
              value={filteredGroupOptions.key}
              onChange={e => handleSearchGroupFilters(e.target.value)}
            />
          </Menu.Item>

          {filteredGroupOptions.key &&
          filteredGroupOptions.items.length === 0 ? (
            <EmptySearchResults>
              <FormattedMessage
                id="ApiKeys.message.noMatchingGroups"
                values={{ group: filteredGroupOptions.key }}
              />
            </EmptySearchResults>
          ) : (
            <Menu.Item key="groups-options-container">
              <StyledCheckboxGroup
                value={filters.group}
                data-testid="api-key-group_filterOptions"
              >
                {options.length > 0 &&
                  options.map((option, index) => (
                    <StyledCheckbox
                      onChange={e => setSelectedFilter(e)}
                      key={index}
                      value={option.value}
                    >
                      {option.label}
                    </StyledCheckbox>
                  ))}
              </StyledCheckboxGroup>
            </Menu.Item>
          )}
          <Menu.Item
            data-testid="api-key-group_resetFilterOptions"
            key="reset-filters"
            onClick={() => {
              setFilters((prevState: any) => ({
                ...prevState,
                group: [],
              }));
              setIsFilteringGroups(false);
              setFilteredGroupOptions({
                key: '',
                items: [],
              });
            }}
          >
            <span id="reset-filter">
              <FormattedMessage id="Global.reset" />
            </span>
          </Menu.Item>
        </Menu>
      </DropdownContainer>
    );
  };

  const redirectToCreateAPIKeyGroup = () => {
    if (parentOrigin && window.top && window.top !== window) {
      window.top.postMessage(
        {
          type: 'redirect',
          url: `/admin/apikeys/create`,
        },
        parentOrigin,
      );
    } else {
      history.push('/apikeys/create');
    }
  };

  return (
    <>
      <div style={{ paddingTop: '28px' }}>
        <FiltersContainer>
          <Row justify="space-between" gutter={0} align="bottom">
            <Col span={12}>
              <Row gutter={[5, 5]}>
                <Col span={12}>
                  <StyledSearchInput
                    onChange={handleSearchApiKey}
                    placeholder={intl.formatMessage({
                      id: 'ApiKeys.filter.search',
                    })}
                    testId="api-key-group_search"
                    prefix={<SearchOutlined />}
                  />
                </Col>
                <Col span={10}>
                  <div>
                    <Dropdown
                      overlay={userGroupsOptions}
                      visible={isFilteringGroups}
                    >
                      <DropdownButton
                        block
                        onClick={e => {
                          setIsFilteringGroups(!isFilteringGroups);
                          e.preventDefault();
                        }}
                        data-testid="api-key-group_filter"
                      >
                        <FormattedMessage id="ApiKeys.filter.filterByUserGroups" />
                        {filters.group.length
                          ? ` (${filters.group.length}) `
                          : ''}
                        <CaretDownOutlined />
                      </DropdownButton>
                    </Dropdown>
                  </div>
                </Col>
              </Row>
            </Col>
            <Col span={10} style={{ textAlign: 'right' }}>
              <Button
                shape="round"
                size="large"
                type="default"
                icon={<PlusCircleFilled />}
                className="action-btn"
                data-testid="api-key-group_new"
                onClick={redirectToCreateAPIKeyGroup}
              >
                <FormattedMessage id="ApiKeys.NewApiKeyGroup.btn" />
              </Button>
            </Col>
          </Row>

          {/* Render list of selections */}
          {!!filters.group.length && (
            <SelectionContainer>
              <SelectionTitle>
                <FormattedMessage id="ApiKeys.filter.currentFilters" />
              </SelectionTitle>
              <SelectionItems className="filter-tag">
                {filters.group.map((filter: any, idx: any) => (
                  <SelectionItem
                    key={`${filter}-${idx}`}
                    className="group-filter-item"
                    data-testid={`group-filter-item-${idx}`}
                  >
                    {filter}
                    <CloseCircleFilled
                      data-testid={`remove-filter-item-${idx}`}
                      onClick={() => deleteItemFromFilter(idx)}
                    />
                  </SelectionItem>
                ))}
              </SelectionItems>

              {/* Clear all button */}
              <SelectionButton
                data-testid="clear-filters"
                type="link"
                onClick={() => {
                  setFilters((prevState: any) => ({
                    ...prevState,
                    group: [],
                  }));
                }}
              >
                <FormattedMessage id="ApiKeys.filter.clearAll" />
                <CloseCircleFilled />
              </SelectionButton>
            </SelectionContainer>
          )}
        </FiltersContainer>

        <Wrapper>
          {/* List of API key groups */}
          <Table
            className="clickable-table-layout"
            dataSource={groupedAPIKeys}
            columns={columns as any}
            data-testid="api-key-group_list"
            rowClassName={(e: any) =>
              diffToLocalTimeZone(e?.createdAt).seconds <= 15 && !newApiKey
                ? 'highlight-row'
                : ''
            }
            bordered={false}
            size="middle"
            loading={loading}
            pagination={{
              showSizeChanger: true,
              showQuickJumper: true,
              size: 'default',
            }}
            onRow={(record: any, index) => {
              return {
                id: `apikey-item-${index}`,
                onClick: () => {
                  if (parentOrigin && window.top && window.top !== window) {
                    window.top.postMessage(
                      {
                        type: 'redirect',
                        url: `/admin/apikeys/${record?.name}`,
                      },
                      parentOrigin,
                    );
                  } else {
                    history.push(`/apikeys/${record?.name}`);
                  }
                },
              };
            }}
          />

          {/* Modal for rendering  */}
          <GroupListModal
            visible={!!visibleUserGroups?.name}
            closable={false}
            footer={null}
          >
            <h3 data-testid="api-key-group_viewGroups">
              <FormattedMessage id="ApiKeys.modal.userGroupsList" />
            </h3>

            <div>
              <b>
                <FormattedMessage id="ApiKeys.groupList.label" />
              </b>
              {visibleUserGroups?.name}
            </div>

            <ListContainer
              bordered
              dataSource={visibleUserGroups?.groups}
              renderItem={item => (
                <List.Item className="apikey-group-item">
                  {item as any}
                </List.Item>
              )}
            />

            <Button
              type="primary"
              shape="round"
              block
              data-testid="api-key-group_closeGroups"
              onClick={() => setVisibleUserGroups(null)}
            >
              <FormattedMessage id="Global.close" />
            </Button>
          </GroupListModal>
        </Wrapper>
      </div>

      <StyledModal
        visible={!!newApiKey}
        footer={null}
        closable={false}
        destroyOnClose
      >
        <GeneratedAPIKeyInstance
          apiKey={newApiKey}
          onClose={() => {
            setNewApiKey('');

            message.success(
              intl.formatMessage({
                id: 'ApiKeys.success.message',
              }),
              2,
            );
            if (parentOrigin && window.top && window.top !== window) {
              window.top.postMessage(
                {
                  type: 'redirect',
                  url: `/admin/apikeys`,
                },
                parentOrigin,
              );
            } else {
              history.push('/apikeys');
            }
          }}
        />
      </StyledModal>
    </>
  );
};

export default APIKey;
